import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { directusAPI, fetchCurrentUser } from '../../api';
import { ContentHeader, Button } from '../../ui/atoms';
import createProjectIcon from '../../img/create-project.svg';
import StandardTable from '../../ui/molecules/StandardTable';
import { formatDisplayDateTime } from '../../utils/dateFormatting';

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center h-full text-center py-24">
      <img
        src={createProjectIcon}
        alt=""
        className="mx-auto h-32 w-32 text-primary-400 opacity-60"
        aria-hidden="true"
      />
      <h2 className="text-2xl font-medium text-gray-900 mt-6">{t('crm.emptyState.title')}</h2>
      <p className="mt-2 text-base text-gray-600 max-w-md">{t('crm.emptyState.description')}</p>
    </div>
  );
};

const CRMTable = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('date_created');
  const [sortDirection, setSortDirection] = useState('desc');
  const [resolvedWorkspaceId, setResolvedWorkspaceId] = useState(localStorage.getItem('workspace_id'));
  
  const token = localStorage.getItem('directus_token');

  useEffect(() => {
    const getWorkspaceId = async () => {
      if (!resolvedWorkspaceId) {
        try {
          if (!token) {
            console.log("No token available, cannot fetch workspace");
            return;
          }
          
          const userData = await fetchCurrentUser(['linked_workspace.id']);
          
          if (userData?.linked_workspace?.id) {
            const id = userData.linked_workspace.id;
            localStorage.setItem('workspace_id', id);
            setResolvedWorkspaceId(id);
          }
        } catch (err) {
          console.error('Error fetching workspace ID:', err);
        }
      }
    };
    
    getWorkspaceId();
  }, [resolvedWorkspaceId, token]);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      
      if (!resolvedWorkspaceId) {
        console.log("No workspace ID resolved yet, waiting...");
        setLeads([]);
        setTotalPages(1);
        setLoading(false);
        return;
      }

      if (!token) {
        console.error('No authentication token available');
        setError('Authentication required. Please sign in again.');
        setLoading(false);
        return;
      }
      
      const params = {
        page: currentPage,
        limit: 25,
        sort: `${sortDirection === 'asc' ? '' : '-'}${sortField}`,
        fields: ['*'],
        filter: {
          linked_workspace: {
            _eq: resolvedWorkspaceId
          }
        },
        meta: '*'
      };

      const response = await directusAPI.get('/items/crm', { params });
      
      setLeads(response.data.data || []);
      setTotalPages(Math.ceil((response.data.meta?.total_count || 0) / 25));
    } catch (err) {
      console.error('Error fetching leads:', err);
      
      // Better error handling based on status code
      if (err.response?.status === 403) {
        setError('You do not have permission to access CRM data. Please contact your administrator.');
      } else if (err.response?.status === 401) {
        setError('Your session has expired. Please sign in again.');
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortField, sortDirection, resolvedWorkspaceId, token, t]);

  useEffect(() => {
    // Add a small delay to prevent rapid successive calls
    const timeoutId = setTimeout(() => {
      if (resolvedWorkspaceId && token) {
        fetchLeads();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [fetchLeads, resolvedWorkspaceId, token]);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    return formatDisplayDateTime(dateString, i18n.language);
  }, [i18n.language]);

  const getStatusBadge = useCallback((status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: t('crm.status.draft') },
      new: { color: 'bg-blue-100 text-blue-800', label: t('crm.status.new') },
      contacted: { color: 'bg-yellow-100 text-yellow-800', label: t('crm.status.contacted') },
      qualified: { color: 'bg-green-100 text-green-800', label: t('crm.status.qualified') },
      converted: { color: 'bg-purple-100 text-purple-800', label: t('crm.status.converted') },
      lost: { color: 'bg-red-100 text-red-800', label: t('crm.status.lost') }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  }, [t]);

  const columns = useMemo(() => [
    {
      key: 'date_created',
      title: (
        <span onClick={() => handleSort('date_created')} className="cursor-pointer hover:bg-gray-100">
          {t('crm.table.dateCreated')}
          {sortField === 'date_created' && (
            <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </span>
      ),
      render: (lead) => formatDate(lead.date_created),
      headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
      cellClassName: 'px-6 py-4 whitespace-nowrap text-sm text-gray-900'
    },
    {
      key: 'name',
      title: (
        <span onClick={() => handleSort('lead_first_name')} className="cursor-pointer hover:bg-gray-100">
          {t('crm.table.name')}
          {sortField === 'lead_first_name' && (
            <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </span>
      ),
      render: (lead) => (
        <div>
          <div className="font-medium">
            {lead.lead_first_name} {lead.lead_last_name}
          </div>
        </div>
      ),
      headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
      cellClassName: 'px-6 py-4 whitespace-nowrap text-sm text-gray-900'
    },
    {
      key: 'email',
      title: t('crm.table.email'),
      render: (lead) => lead.lead_email || '-',
      headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
      cellClassName: 'px-6 py-4 whitespace-nowrap text-sm text-gray-900'
    },
    {
      key: 'phone',
      title: t('crm.table.phone'),
      render: (lead) => lead.lead_phone || '-',
      headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
      cellClassName: 'px-6 py-4 whitespace-nowrap text-sm text-gray-900'
    },
    {
      key: 'status',
      title: (
        <span onClick={() => handleSort('status')} className="cursor-pointer hover:bg-gray-100">
          {t('crm.table.status')}
          {sortField === 'status' && (
            <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </span>
      ),
      render: (lead) => getStatusBadge(lead.status),
      headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
      cellClassName: 'px-6 py-4 whitespace-nowrap'
    },
    {
      key: 'sessionId',
      title: t('crm.table.sessionId'),
      render: (lead) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {lead.linked_session ? lead.linked_session.substring(0, 8) + '...' : '-'}
        </code>
      ),
      headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
      cellClassName: 'px-6 py-4 whitespace-nowrap text-sm text-gray-500'
    }
  ], [t, formatDate, handleSort, getStatusBadge, sortField, sortDirection]);

  const tableData = useMemo(() => {
    return leads.map(lead => ({
      ...lead,
      actions: (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/ai-agents/leads/${lead.id}`);
          }}
          className="text-primary-600 hover:text-primary-900 transition-colors duration-200"
        >
          {t('common.viewDetails')}
        </button>
      )
    }));
  }, [leads, t, navigate]);

  if (loading && leads.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <ContentHeader
          title={t('crm.title')}
          description={t('crm.description')}
        />
        <div className="mt-8 text-center py-24">
          <div className="flex flex-col items-center">
            <svg className="h-12 w-12 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.73-.833-2.5 0L4.314 16.5c-.77.833-.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Error</h3>
            <p className="text-gray-600 mb-4 max-w-md">{error}</p>
            <div className="flex space-x-3">
              <button 
                onClick={fetchLeads}
                className="btn btn-md btn-rounded btn-primary"
              >
                {t('common.tryAgain')}
              </button>
              <button 
                onClick={() => window.location.href = '/'}
                className="btn btn-md btn-rounded btn-secondary"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent min-h-full">
      <ContentHeader
        title={t('crm.title')}
        description={t('crm.description')}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {!resolvedWorkspaceId && !loading && (
        <div className="mt-8 text-center py-24">
          <div className="flex flex-col items-center">
            <svg className="h-12 w-12 text-yellow-400 mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Workspace Detected</h3>
            <p className="text-gray-600 max-w-md">
              {t('crm.noWorkspace')}
            </p>
          </div>
        </div>
      )}

      {leads.length === 0 && !loading && !error && resolvedWorkspaceId && (
        <EmptyState />
      )}

      {(leads.length > 0 || loading) && (
        <div className="mt-8">
          <StandardTable
            columns={columns}
            data={tableData}
            loading={loading}
            onRowClick={(lead) => navigate(`/ai-agents/leads/${lead.id}`)}
          />
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <Button
              variant="secondary"
              size="md"
              shape="square"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              {t('common.pagination.previous')}
            </Button>
            <Button
              variant="secondary"
              size="md"
              shape="square"
              className="ml-3"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              {t('common.pagination.next')}
            </Button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {t('common.pagination.showing')} {t('common.pagination.page')}{' '}
                <span className="font-medium">{currentPage}</span>{' '}
                {t('common.pagination.of')}{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <Button
                  variant="secondary"
                  size="md"
                  shape="square"
                  className="rounded-r-none"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {t('common.pagination.previous')}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  shape="square"
                  className="rounded-l-none -ml-px"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t('common.pagination.next')}
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CRMTable;